import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceStatus } from "@/models/enums/service";
import { mergeObjects } from "@/utils/utils";
import Service from "@/services";
import {
  ProblemModel,
  ChannelModel,
  StatusModel,
  ProblemGroupModel,
  ProblemTypeModel,
  CustomerWithProblemModel,
  ProblemTrackingCustomerModel,
  ProblemByCustomerModel,
} from "@/models/classes/problem_tracking/problem_tracking";

const service = new Service.ProblemTrackingService();

// Interface
interface ProblemTrackingState {
  channels: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ChannelModel[];
  };
  statuses: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: StatusModel[];
  };
  problemStatuses: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: StatusModel[];
  };
  problemGroups: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemGroupModel[];
  };
  problemTypes: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemTypeModel[];
  };
  customers: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemTrackingCustomerModel[];
    total: number;
  };
  reportCustomers: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemTrackingCustomerModel[];
    total: number;
  };
  customersWithProblems: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: CustomerWithProblemModel[];
    total: number;
    hasMore: boolean;
  };
  problems: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemModel[];
    total: number;
    hasMore: boolean;
    isOwnCustomer: boolean;
  };
  currentProblem: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: ProblemModel | null;
  };
}

// Initialize State
const initialState: ProblemTrackingState = {
  channels: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
  },
  statuses: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
  },
  problemStatuses: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
  },
  problemGroups: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
  },
  problemTypes: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
  },
  customers: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
  },
  reportCustomers: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
  },
  customersWithProblems: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
    hasMore: false,
  },
  problems: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
    hasMore: false,
    isOwnCustomer: false,
  },
  currentProblem: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
  },
};

// Reducer
export const slice = createSlice({
  name: "ProblemTrackingState",
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
  },
  extraReducers: (builder) => {
    // Channels
    builder.addCase(onFetchChannels.pending, (state) => {
      state.channels.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchChannels.fulfilled, (state, action) => {
      state.channels.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.channels.data = action.payload.data;
      }
    });
    builder.addCase(onFetchChannels.rejected, (state, action) => {
      state.channels.network.code = ServiceStatus.failed;
      state.channels.network.error =
        action.error.message || "Failed to fetch channels";
    });

    // Statuses
    builder.addCase(onFetchStatuses.pending, (state) => {
      state.statuses.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchStatuses.fulfilled, (state, action) => {
      state.statuses.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.statuses.data = action.payload.data;
      }
    });
    builder.addCase(onFetchStatuses.rejected, (state, action) => {
      state.statuses.network.code = ServiceStatus.failed;
      state.statuses.network.error =
        action.error.message || "Failed to fetch statuses";
    });

    // Statuses With Problems
    builder.addCase(onFetchStatusesWithProblems.pending, (state) => {
      state.problemStatuses.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchStatusesWithProblems.fulfilled, (state, action) => {
      state.problemStatuses.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.problemStatuses.data = action.payload.data;
      }
    });
    builder.addCase(onFetchStatusesWithProblems.rejected, (state, action) => {
      state.problemStatuses.network.code = ServiceStatus.failed;
      state.problemStatuses.network.error =
        action.error.message || "Failed to fetch statuses with problems";
    });

    // Problem Groups
    builder.addCase(onFetchProblemGroups.pending, (state) => {
      state.problemGroups.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchProblemGroups.fulfilled, (state, action) => {
      state.problemGroups.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.problemGroups.data = action.payload.data;
      }
    });
    builder.addCase(onFetchProblemGroups.rejected, (state, action) => {
      state.problemGroups.network.code = ServiceStatus.failed;
    });

    // Problem Types
    builder.addCase(onFetchProblemTypes.pending, (state) => {
      state.problemTypes.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchProblemTypes.fulfilled, (state, action) => {
      state.problemTypes.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.problemTypes.data = action.payload.data;
      }
    });
    builder.addCase(onFetchProblemTypes.rejected, (state, action) => {
      state.problemTypes.network.code = ServiceStatus.failed;
    });

    // Customers (for ComboBox)
    builder.addCase(onFetchCustomers.pending, (state) => {
      state.customers.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchCustomers.fulfilled, (state, action) => {
      state.customers.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.response && action.payload.response.result) {
        const { response, params } = action.payload;
        const total = response.xTotalCount || response.data.length;
        if (params.offset === 0) {
          state.customers.data = response.data;
        } else {
          state.customers.data = [
            ...(state.customers.data || []),
            ...response.data,
          ];
        }
        state.customers.total = total;
      }
    });
    builder.addCase(onFetchCustomers.rejected, (state, action) => {
      state.customers.network.code = ServiceStatus.failed;
    });

    // Report Customers (for ComboBox)
    builder.addCase(onFetchReportCustomers.pending, (state) => {
      state.reportCustomers.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchReportCustomers.fulfilled, (state, action) => {
      state.reportCustomers.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.response && action.payload.response.result) {
        const { response, params } = action.payload;
        const total = response.xTotalCount || response.data.length;
        if (params.offset === 0) {
          state.reportCustomers.data = response.data;
        } else {
          state.reportCustomers.data = [
            ...(state.reportCustomers.data || []),
            ...response.data,
          ];
        }
        state.reportCustomers.total = total;
      }
    });
    builder.addCase(onFetchReportCustomers.rejected, (state, action) => {
      state.reportCustomers.network.code = ServiceStatus.failed;
    });

    // Customers With Problems
    builder.addCase(onFetchCustomersWithProblems.pending, (state) => {
      state.customersWithProblems.network.code = ServiceStatus.loading;
      state.customersWithProblems.data = [];
      state.customersWithProblems.hasMore = false;
    });
    builder.addCase(onFetchCustomersWithProblems.fulfilled, (state, action) => {
      state.customersWithProblems.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.customersWithProblems.data = action.payload.data;
        state.customersWithProblems.total =
          action.payload.xTotalCount || action.payload.data.length;
        state.customersWithProblems.hasMore =
          state.customersWithProblems.data.length <
          state.customersWithProblems.total;
      }
    });
    builder.addCase(onFetchCustomersWithProblems.rejected, (state, action) => {
      state.customersWithProblems.network.code = ServiceStatus.failed;
      state.customersWithProblems.network.error =
        action.error.message || "Failed to fetch customers with problems";
    });

    // More Customers With Problems
    builder.addCase(onFetchMoreCustomersWithProblems.pending, (state) => {});
    builder.addCase(
      onFetchMoreCustomersWithProblems.fulfilled,
      (state, action) => {
        if (action.payload && action.payload.result) {
          state.customersWithProblems.data.push(...action.payload.data);
          state.customersWithProblems.total =
            action.payload.xTotalCount ||
            state.customersWithProblems.data.length;
          state.customersWithProblems.hasMore =
            state.customersWithProblems.data.length <
            state.customersWithProblems.total;
        }
      },
    );
    builder.addCase(
      onFetchMoreCustomersWithProblems.rejected,
      (state, action) => {},
    );

    // Problems
    builder.addCase(onFetchProblemsByCustomer.pending, (state) => {
      state.problems.network.code = ServiceStatus.loading;
      state.problems.data = [];
      state.problems.hasMore = false;
    });
    builder.addCase(onFetchProblemsByCustomer.fulfilled, (state, action) => {
      state.problems.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        const data = action.payload.data as ProblemByCustomerModel;
        state.problems.data = data?.items ?? [];
        state.problems.isOwnCustomer = data?.isOwnCustomer ?? false;
        state.problems.total =
          action.payload.xTotalCount || state.problems.data.length;
        state.problems.hasMore =
          state.problems.data.length < state.problems.total;
      }
    });
    builder.addCase(onFetchProblemsByCustomer.rejected, (state, action) => {
      state.problems.network.code = ServiceStatus.failed;
      state.problems.network.error =
        action.error.message || "Failed to fetch problems";
    });

    // More Problems
    builder.addCase(onFetchMoreProblemsByCustomer.pending, (state) => {});
    builder.addCase(
      onFetchMoreProblemsByCustomer.fulfilled,
      (state, action) => {
        if (action.payload && action.payload.result) {
          const data = action.payload.data as ProblemByCustomerModel;
          state.problems.data.push(...(data?.items ?? []));
          state.problems.total =
            action.payload.xTotalCount || state.problems.data.length;
          state.problems.hasMore =
            state.problems.data.length < state.problems.total;
        }
      },
    );
    builder.addCase(
      onFetchMoreProblemsByCustomer.rejected,
      (state, action) => {},
    );

    // Problem Detail
    builder.addCase(onFetchProblemDetail.pending, (state) => {
      state.currentProblem.network.code = ServiceStatus.loading;
      state.currentProblem.data = null;
    });
    builder.addCase(onFetchProblemDetail.fulfilled, (state, action) => {
      state.currentProblem.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.result) {
        state.currentProblem.data = action.payload.data;
      }
    });
    builder.addCase(onFetchProblemDetail.rejected, (state, action) => {
      state.currentProblem.network.code = ServiceStatus.failed;
      state.currentProblem.network.error =
        action.error.message || "Failed to fetch problem detail";
    });
  },
});

// Thunks
export const onFetchChannels = createAsyncThunk(
  "problem_tracking/channels",
  async (_, thunkAPI) => {
    try {
      return await service.getChannels();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchStatuses = createAsyncThunk(
  "problem_tracking/statuses",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as any;
    if (state.problemTracking.statuses.data.length > 0) {
      return {
        result: true,
        data: state.problemTracking.statuses.data,
      };
    }
    try {
      return await service.getStatuses();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchStatusesWithProblems = createAsyncThunk(
  "problem_tracking/statusesWithProblems",
  async (problemId: number, thunkAPI) => {
    try {
      return await service.getStatusesWithProblems(problemId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchProblemGroups = createAsyncThunk(
  "problem_tracking/problemGroups",
  async (_, thunkAPI) => {
    try {
      return await service.getProblemGroups();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchProblemTypes = createAsyncThunk(
  "problem_tracking/problemTypes",
  async (_, thunkAPI) => {
    try {
      return await service.getProblemTypes();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchCustomers = createAsyncThunk(
  "problem_tracking/customers",
  async (
    params: { customerSearchText: string; limit?: number; offset?: number },
    thunkAPI,
  ) => {
    try {
      const response = await service.getCustomers(params);
      return { response, params };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchReportCustomers = createAsyncThunk(
  "problem_tracking/reportCustomers",
  async (
    params: { customerSearchText: string; limit?: number; offset?: number },
    thunkAPI,
  ) => {
    try {
      const response = await service.getDropdownCustomersForReport(params);
      return { response, params };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchCustomersWithProblems = createAsyncThunk(
  "problem_tracking/customersWithProblems",
  async (params: any, thunkAPI) => {
    try {
      return await service.getCustomersWithProblems(params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchMoreCustomersWithProblems = createAsyncThunk(
  "problem_tracking/customersWithProblems/more",
  async (params: any, thunkAPI) => {
    try {
      return await service.getCustomersWithProblems(params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchProblemsByCustomer = createAsyncThunk(
  "problem_tracking/problemsByCustomer",
  async (params: any, thunkAPI) => {
    try {
      return await service.getProblemsByCustomer(params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchMoreProblemsByCustomer = createAsyncThunk(
  "problem_tracking/problemsByCustomer/more",
  async (params: any, thunkAPI) => {
    try {
      return await service.getProblemsByCustomer(params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onCreateProblem = createAsyncThunk(
  "problem_tracking/create",
  async (payload: any, thunkAPI) => {
    try {
      return await service.createProblem(payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onResolveProblem = createAsyncThunk(
  "problem_tracking/resolve",
  async (
    { problemId, payload }: { problemId: number; payload: any },
    thunkAPI,
  ) => {
    try {
      return await service.resolveProblem(problemId, payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onApproveProblem = createAsyncThunk(
  "problem_tracking/approve",
  async (
    {
      problemId,
      payload,
    }: { problemId: number; payload: { isApprove: boolean } },
    thunkAPI,
  ) => {
    try {
      return await service.approveProblem(problemId, payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const onFetchProblemDetail = createAsyncThunk(
  "problem_tracking/detail",
  async (problemId: number, thunkAPI) => {
    try {
      return await service.getProblemDetail(problemId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Actions
export const { reset, patch } = slice.actions;

// Export
export default slice.reducer;
